import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Globe, ChevronRight, Shield, Clock, Bell, TrendingUp, Star, Sparkles } from 'lucide-react';

export const Medscore = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id^="feature-"]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Comprehensive Credit Scores",
      description: "View comprehensive credit scores of pharmacies with precise analytics",
      gradient: "from-emerald-500 to-teal-600"
    },
    {
      icon: <Clock className="w-7 h-7" />,
      title: "Payment Monitoring",
      description: "Monitor payment delay patterns and total outstanding amounts",
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      icon: <Bell className="w-7 h-7" />,
      title: "Automated Reminders",
      description: "Send automated payment reminders and default reports to pharmacies",
      gradient: "from-purple-500 to-pink-600"
    },
    {
      icon: <Clock className="w-7 h-7" />,
      title: "License Expiry Alerts",
      description: "Receive timely alerts for drug license expiry dates (3 months in advance)",
      gradient: "from-orange-500 to-red-600"
    },
    {
      icon: <TrendingUp className="w-7 h-7" />,
      title: "Network Expansion",
      description: "Expand distribution networks confidently with reliable risk assessment",
      gradient: "from-cyan-500 to-blue-600"
    },
    {
      icon: <Star className="w-7 h-7" />,
      title: "Pharmacy Ratings",
      description: "Access pharmacy ratings of distributors based on product quality, delivery speed, and discount offerings",
      gradient: "from-amber-500 to-yellow-600"
    }
  ];

  return (
    <div className="w-full min-h-screen bg-slate-950 text-white relative overflow-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;500;600;700;800&display=swap');
        
        * {
          font-family: 'Syne', sans-serif;
        }
        
        .mono {
          font-family: 'Space Mono', monospace;
        }
        
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes rotate-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .gradient-bg {
          background: linear-gradient(-45deg, #0f172a, #1e293b, #334155, #1e3a8a);
          background-size: 400% 400%;
          animation: gradient-shift 15s ease infinite;
        }
        
        .feature-card {
          opacity: 0;
          transform: translateY(40px);
        }
        
        .feature-card.visible {
          animation: slide-up 0.6s ease forwards;
        }
        
        .glass {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .glow-border {
          position: relative;
          overflow: hidden;
        }
        
        .glow-border::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, transparent, rgba(59, 130, 246, 0.5), transparent);
          border-radius: inherit;
          animation: rotate-slow 4s linear infinite;
          z-index: -1;
        }
        
        .text-gradient {
          background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #f0abfc 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .grid-pattern {
          background-image: 
            linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
        }
        
        .orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.3;
          animation: pulse-glow 8s ease-in-out infinite;
        }
      `}</style>

      {/* Animated Background Orbs */}
      <div className="orb w-96 h-96 bg-blue-500 top-20 -left-48" style={{animationDelay: '0s'}}></div>
      <div className="orb w-80 h-80 bg-purple-500 top-1/3 -right-40" style={{animationDelay: '2s'}}></div>
      <div className="orb w-72 h-72 bg-cyan-500 bottom-1/4 left-1/4" style={{animationDelay: '4s'}}></div>
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"></div>
      
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
        {/* Hero Section */}
        <div className="flex flex-col lg:flex-row items-center gap-16 mb-32">
          <div className="lg:w-1/2 space-y-8" style={{animation: 'slide-up 1s ease'}}>
            <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-sm text-blue-300 mono">
              <Sparkles size={16} />
              <span>GLOBAL PHARMA CREDIT BENCHMARK</span>
            </div>
            
            <h1 className="text-7xl md:text-8xl font-bold tracking-tight leading-none">
              <span className="text-gradient">MedScore</span>
            </h1>
            
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-200">
                Simplify Credit.<br/>
                Build Trust.<br/>
                Empower Pharma.
              </h2>
              
              <p className="text-lg text-slate-400 leading-relaxed max-w-xl">
                The world's first centralized Credit Risk Assessment & Management platform 
                tailored for the pharmaceutical industry. Revolutionizing credit-based transactions 
                by providing pharmaceutical distributors with reliable, data-driven credit scores.
              </p>
            </div>
            
            <div className="flex flex-wrap gap-4 pt-4">
              <a 
                href="https://www.medscore.in" 
                className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/50 overflow-hidden"
              >
                <span className="relative z-10">Visit Platform</span>
                <ChevronRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </a>
              
              <button className="inline-flex items-center gap-2 glass px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all">
                <span>Learn More</span>
              </button>
            </div>
          </div>
          
          <div className="lg:w-1/2 flex justify-center" style={{animation: 'slide-up 1s ease 0.2s backwards'}}>
            <div className="relative w-full max-w-md aspect-square">
              {/* Rotating Ring */}
              <div className="absolute inset-0 rounded-full border-2 border-blue-500/30" style={{animation: 'rotate-slow 20s linear infinite'}}></div>
              <div className="absolute inset-4 rounded-full border-2 border-purple-500/20" style={{animation: 'rotate-slow 15s linear infinite reverse'}}></div>
              
              {/* Central Logo Container */}
              <div className="absolute inset-8 glass rounded-full flex items-center justify-center p-8 glow-border" style={{animation: 'float 6s ease-in-out infinite'}}>
                <img 
                  src='M1WhiteLogo.png' 
                  className='w-full h-full object-contain drop-shadow-2xl' 
                  alt="MedScore Logo"
                />
              </div>
              
              {/* Floating Elements */}
              <div className="absolute top-0 right-0 glass p-3 rounded-xl" style={{animation: 'float 4s ease-in-out infinite'}}>
                <Shield className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="absolute bottom-0 left-0 glass p-3 rounded-xl" style={{animation: 'float 5s ease-in-out infinite 1s'}}>
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <div className="absolute top-1/3 -left-4 glass p-3 rounded-xl" style={{animation: 'float 4.5s ease-in-out infinite 0.5s'}}>
                <Star className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Quote Section */}
        <div 
          className="glass rounded-3xl p-12 mb-32 relative overflow-hidden group hover:bg-white/5 transition-all"
          style={{animation: 'slide-up 1s ease 0.4s backwards'}}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="relative z-10">
            <div className="text-6xl text-blue-400/20 mb-4">"</div>
            <blockquote className="text-2xl md:text-3xl font-medium text-slate-200 leading-relaxed italic max-w-4xl">
              We're setting a global benchmark for credit risk management in the pharmaceutical sector—simplifying credit, amplifying trust.
            </blockquote>
            <div className="flex items-center gap-3 mt-8">
              <div className="w-12 h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
              <span className="mono text-sm text-slate-400">MEDSCORE VISION</span>
            </div>
          </div>
        </div>
        
        {/* Features Section */}
        <div className="mb-20">
          <div className="text-center mb-16" style={{animation: 'slide-up 1s ease 0.6s backwards'}}>
            <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-sm text-blue-300 mono mb-6">
              <Star size={16} />
              <span>POWERFUL FEATURES</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="text-gradient">Everything You Need</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Comprehensive tools designed to transform pharmaceutical credit management
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                id={`feature-${index}`}
                className={`feature-card group ${isVisible[`feature-${index}`] ? 'visible' : ''}`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className="glass rounded-2xl p-8 h-full hover:bg-white/5 transition-all hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20 relative overflow-hidden">
                  {/* Gradient Background on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  
                  <div className="relative z-10">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br ${feature.gradient} mb-6 group-hover:scale-110 transition-transform`}>
                      {React.cloneElement(feature.icon, { className: 'w-7 h-7 text-white' })}
                    </div>
                    
                    <h3 className="text-xl font-bold text-slate-100 mb-3 group-hover:text-white transition-colors">
                      {feature.title}
                    </h3>
                    
                    <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Stats Section */}
        <div className="glass rounded-3xl p-12 mb-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { value: "100%", label: "Pharma Focused" },
            { value: "24/7", label: "Real-time Monitoring" },
            { value: "∞", label: "Scalability" }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-5xl md:text-6xl font-bold text-gradient mb-2">{stat.value}</div>
              <div className="text-slate-400 mono text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className="glass rounded-3xl p-16 text-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your <span className="text-gradient">Credit Management?</span>
            </h2>
            <p className="text-xl text-slate-400 mb-8">
              Join the future of pharmaceutical credit assessment today
            </p>
            <a 
              href="https://www.medscore.in" 
              className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 px-10 py-5 rounded-full font-semibold text-lg transition-all hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/50"
            >
              <span>Get Started Now</span>
              <ChevronRight size={20} />
            </a>
          </div>
        </div>
        
        <div className="text-center mt-20">
          <div className="inline-flex items-center gap-3 text-slate-500 mono text-sm">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
            <span>PCRA Technologies • 2025</span>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};